/**
 * PermissionGuard — route/section-level RBAC gate.
 *
 * Renders children only when the logged-in user has READ access to the screen's
 * job (holds "<RESOURCE>.READ", resolved from constants/permissions). While
 * permissions are still loading it shows a spinner; if access is denied it shows
 * an Access Denied panel (or the caller-supplied fallback).
 *
 * Write access (create/update/delete) is enforced by the action buttons (via the
 * useCan* hooks) and by the backend's require_permission — a viewer with READ can
 * open a screen but cannot mutate it.
 */
import { ReactNode } from 'react';
import { ProgressSpinner } from 'primereact/progressspinner';
import { useMyPermissions } from '../../hooks/usePermissions';

interface PermissionGuardProps {
  /** Guard by a permission NAME directly, e.g. PERMISSIONS.APP_READ (preferred). */
  permission?: string;
  /** Legacy: guard by job (checks that job's READ permission). */
  jobName?: string;
  children: ReactNode;
  fallback?: ReactNode;
  resourceName?: string;
}

const AccessDenied = ({ resourceName }: { resourceName?: string }) => (
  <div className="flex flex-col items-center justify-center min-h-[300px] text-center px-4">
    <div className="w-14 h-14 rounded-full bg-red-100 dark:bg-red-900/40 flex items-center justify-center mb-3">
      <i className="pi pi-lock text-2xl text-red-500" />
    </div>
    <h2 className="text-lg font-semibold text-ink">Access denied</h2>
    <p className="text-sm text-ink-muted mt-1 max-w-md">
      You don't have permission to view {resourceName || 'this page'}. Contact an administrator
      if you believe this is a mistake.
    </p>
  </div>
);

export const PermissionGuard = ({ permission, jobName, children, fallback, resourceName }: PermissionGuardProps) => {
  const { has, can, isLoaded, isLoading } = useMyPermissions();

  // Still resolving the user's permissions — don't flash the page or a denial.
  if (isLoading && !isLoaded) {
    return (
      <div className="flex justify-center items-center min-h-[300px]">
        <ProgressSpinner style={{ width: '40px', height: '40px' }} />
      </div>
    );
  }

  // Prefer a direct permission name; fall back to the job's READ permission.
  const allowed = permission ? has(permission) : jobName ? can(jobName, 'read') : true;
  if (!allowed) {
    return <>{fallback ?? <AccessDenied resourceName={resourceName} />}</>;
  }

  return <>{children}</>;
};

export default PermissionGuard;
